import type { TakContact, TakHealthResponse, TakMission, EchoMission, EchoMissionDetail, EchoJoinChannelResponse, ReplayFrame } from '@vtak/shared';
import { CLIENT_CONFIG } from '@/config';
import { useWorkspaceStore } from '@/stores/workspace-store';

/** Stored auth token (access token) for API requests */
let authToken = '';

/** Stored id_token, forwarded to Echo so it can authenticate the user */
let echoIdToken = '';

/** Set the auth token used for all API requests */
export function setApiToken(token: string): void {
  authToken = token;
}

/** Current access token — used by the WebSocket handshake subprotocol */
export function getApiToken(): string {
  return authToken;
}

/**
 * Set the id_token forwarded to Echo. Echo's Login RPC verifies the id_token
 * (not the access token), so the server needs it to open a per-user session.
 */
export function setEchoIdToken(token: string): void {
  echoIdToken = token;
}

/**
 * The active unit id for the `X-Vtak-Unit` hint — the REST twin of
 * /api/config's `?unit=` pin, so unit-scoped server resolution (plugin REST
 * entitlement) matches the manifest the client rendered. Read via getState()
 * at call time — never at module init — so the store import cannot introduce
 * an initialization-order cycle; tolerant of non-browser test contexts.
 */
function activeUnitId(): string | null {
  try {
    return useWorkspaceStore.getState().selectedUnitId;
  } catch {
    return null;
  }
}

/** Create fetch init with Authorization (+ Echo id_token, active unit) headers when available */
export function authHeaders(init?: RequestInit): RequestInit {
  const headers = new Headers(init?.headers);
  if (authToken) {
    headers.set('Authorization', `Bearer ${authToken}`);
  }
  if (echoIdToken) {
    headers.set('X-Echo-Id-Token', echoIdToken);
  }
  const unitId = activeUnitId();
  if (unitId) {
    headers.set('X-Vtak-Unit', unitId);
  }
  return { ...init, headers };
}

/**
 * Authenticated JSON fetch with a fallback on any failure (keeps panels
 * resilient). All /api requests must go through this or authHeaders() —
 * a bare fetch() is rejected with 401 once real auth is on.
 */
export async function fetchJson<T>(url: string, fallback: T): Promise<T> {
  try {
    const res = await fetch(`${CLIENT_CONFIG.apiBase}${url}`, authHeaders());
    return res.ok ? ((await res.json()) as T) : fallback;
  } catch {
    return fallback;
  }
}

/** Typed fetch wrapper for all API endpoints */
export const apiClient = {
  /** Check TAK Server health */
  async checkHealth(): Promise<TakHealthResponse> {
    const res = await fetch(`${CLIENT_CONFIG.apiBase}/api/health`, authHeaders());
    return res.json() as Promise<TakHealthResponse>;
  },

  /** Get all TAK contacts */
  async getContacts(): Promise<TakContact[]> {
    const res = await fetch(`${CLIENT_CONFIG.apiBase}/api/contacts`, authHeaders());
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data) ? data as TakContact[] : [];
  },

  /** Get CoT situational awareness data */
  async getCotSa(): Promise<unknown> {
    const res = await fetch(`${CLIENT_CONFIG.apiBase}/api/cot/sa`, authHeaders());
    if (!res.ok) return [];
    return res.json();
  },

  /** Get all missions */
  async getMissions(): Promise<TakMission[]> {
    const res = await fetch(`${CLIENT_CONFIG.apiBase}/api/missions`, authHeaders());
    if (!res.ok) return [];
    const data = await res.json();
    return extractMissions(data);
  },

  /** Create a mission by name */
  async createMission(name: string): Promise<boolean> {
    const res = await fetch(
      `${CLIENT_CONFIG.apiBase}/api/missions/${encodeURIComponent(name)}`,
      authHeaders({ method: 'POST' }),
    );
    return res.ok;
  },

  /** Get active Echo missions */
  async getEchoMissions(): Promise<EchoMission[]> {
    const res = await fetch(`${CLIENT_CONFIG.apiBase}/api/echo/missions`, authHeaders());
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data) ? data as EchoMission[] : [];
  },

  /** Get full mission detail (channels + analysis) by join code */
  async getEchoMissionDetail(joinCode: string): Promise<EchoMissionDetail | null> {
    const res = await fetch(`${CLIENT_CONFIG.apiBase}/api/echo/missions/${encodeURIComponent(joinCode)}`, authHeaders());
    if (!res.ok) return null;
    return res.json() as Promise<EchoMissionDetail>;
  },

  /** Request view-only guest access to a mission (from a scanned join QR) */
  async requestGuestAccess(joinCode: string): Promise<boolean> {
    const res = await fetch(
      `${CLIENT_CONFIG.apiBase}/api/echo/missions/${encodeURIComponent(joinCode)}/guest-request`,
      authHeaders({ method: 'POST' }),
    );
    return res.ok;
  },

  /** The caller's own guest access state for a mission (pending/approved + audio) */
  async getGuestAccess(joinCode: string): Promise<{ status: string; audioGranted: boolean }> {
    const res = await fetch(
      `${CLIENT_CONFIG.apiBase}/api/echo/missions/${encodeURIComponent(joinCode)}/guest-access`,
      authHeaders(),
    );
    if (!res.ok) return { status: 'none', audioGranted: false };
    return res.json() as Promise<{ status: string; audioGranted: boolean }>;
  },

  /** Create a new Echo mission */
  async createEchoMission(name: string): Promise<EchoMission | null> {
    const res = await fetch(`${CLIENT_CONFIG.apiBase}/api/echo/missions`, authHeaders({
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name }),
    }));
    if (!res.ok) return null;
    return res.json() as Promise<EchoMission>;
  },

  /** Join an Echo channel for live audio — returns LiveKit token + WS URL */
  async joinChannelLive(channelId: number, joinCode: string): Promise<EchoJoinChannelResponse | null> {
    const res = await fetch(
      `${CLIENT_CONFIG.apiBase}/api/echo/channels/${channelId}/join-live?join_code=${encodeURIComponent(joinCode)}`,
      authHeaders({ method: 'POST' }),
    );
    if (!res.ok) return null;
    return res.json() as Promise<EchoJoinChannelResponse>;
  },

  /** Get historical CoT events for a UID */
  async getUidHistory(uid: string): Promise<ReplayFrame[]> {
    const res = await fetch(`${CLIENT_CONFIG.apiBase}/api/cot/${encodeURIComponent(uid)}/history`, authHeaders());
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data) ? data as ReplayFrame[] : [];
  },

  /** Send a CoT event (XML body) */
  async sendCot(xml: string): Promise<{ ok: boolean; status: number; body: string }> {
    const res = await fetch(`${CLIENT_CONFIG.apiBase}/api/cot`, authHeaders({
      method: 'POST',
      headers: { 'Content-Type': 'application/xml' },
      body: xml,
    }));
    const body = await res.text();
    return { ok: res.ok, status: res.status, body };
  },
};

/** Extract missions array from TAK's varying response shapes */
function extractMissions(data: unknown): TakMission[] {
  if (Array.isArray(data)) return data as TakMission[];
  if (data && typeof data === 'object') {
    const obj = data as Record<string, unknown>;
    if (Array.isArray(obj['data'])) return obj['data'] as TakMission[];
    if (obj['data']) return [obj['data'] as TakMission];
    if (Array.isArray(obj['missions'])) return obj['missions'] as TakMission[];
  }
  return [];
}
